import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CustomStatus, MetaStatus, StatusType } from '../../../../core/models/models';
import { CustomStatusService } from '../../../../core/services/custom-status.service';
import { ConfirmationDialogComponent } from '../../../../shared/components/dialogs/confirmation-dialog.component';

@Component({
  selector: 'app-status-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatIconModule, MatFormFieldModule, MatSelectModule, ConfirmationDialogComponent],
  templateUrl: './status-dialog.component.html',
  styleUrl: './status-dialog.component.scss'
})
export class StatusDialogComponent implements OnInit {
  statusForm: FormGroup;
  metaStatuses = Object.values(MetaStatus);
  statusTypes = Object.values(StatusType);
  errorMessage: string = '';
  isDeleteConfirmOpen = false;
  canDeleteStatus = false;

  constructor(
    private fb: FormBuilder,
    private customStatusService: CustomStatusService,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<StatusDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CustomStatus
  ) {
    this.statusForm = this.fb.group({
      name: [data.name || '', Validators.required],
      metaStatus: [data.metaStatus || '', Validators.required],
      type: [data.type || StatusType.PROJECT, Validators.required],
      color: [data.color || '#64748b']
    });
  }

  ngOnInit(): void {
  }

  onSave(): void {
    if (this.statusForm.valid) {
      const status: CustomStatus = {
        ...this.data,
        ...this.statusForm.value
      };

      // Ensure color is a string
      if (status.color && typeof status.color !== 'string') {
        status.color = (status.color as any).toString();
      }

      if (status.id) {
        this.customStatusService.update(status.id, status).subscribe({
          next: () => this.dialogRef.close(true),
          error: (err) => {
            console.error('Error updating status', err);
            this.errorMessage = err?.error?.message || err?.error || 'Erreur lors de la mise à jour du statut';
            this.cdr.detectChanges();
          }
        });
      } else {
        this.customStatusService.create(status).subscribe({
          next: () => this.dialogRef.close(true),
          error: (err) => {
            console.error('Error creating status', err);
            this.errorMessage = err?.error?.message || err?.error || 'Erreur lors de la création du statut';
            this.cdr.detectChanges();
          }
        });
      }
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onRequestDelete(): void {
    if (!this.data?.id) {
      return;
    }
    this.customStatusService.canDelete(this.data.id).subscribe({
      next: (canDelete) => {
        this.canDeleteStatus = canDelete;
        this.isDeleteConfirmOpen = true;
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMessage = 'Impossible de vérifier le mode de suppression du statut';
        this.cdr.detectChanges();
      }
    });
  }

  confirmDelete(): void {
    if (!this.data?.id) {
      return;
    }
    this.customStatusService.delete(this.data.id).subscribe({
      next: () => this.dialogRef.close(true),
      error: (err) => {
        this.errorMessage = this.extractDeleteErrorMessage(err);
        this.isDeleteConfirmOpen = false;
        this.cdr.detectChanges();
      }
    });
  }

  cancelDelete(): void {
    this.isDeleteConfirmOpen = false;
    this.cdr.detectChanges();
  }

  getDeleteDialogTitle(): string {
    return this.canDeleteStatus ? 'Supprimer le statut' : 'Archiver le statut';
  }

  getDeleteDialogLabel(): string {
    return this.canDeleteStatus ? 'Supprimer' : 'Archiver';
  }

  getDeleteDialogMessage(): string {
    const name = this.data?.name ?? '';
    if (this.canDeleteStatus) {
      return `Voulez-vous vraiment supprimer le statut "${name}" ? Cette action est irreversible.`;
    }
    return `Le statut "${name}" est lie a des objets. Voulez-vous l'archiver ?`;
  }

  private extractDeleteErrorMessage(err: any): string {
    const fallback = 'Erreur lors de la suppression/archivage du statut';
    const raw = err?.error?.message ?? err?.error?.error ?? err?.error ?? err?.message ?? fallback;
    const message = typeof raw === 'string' ? raw : (raw?.message || raw?.error || fallback);
    if (message.toLowerCase().includes('internal server error') || message === 'Error') {
      const targetMetaStatus = this.data?.metaStatus;
      if (targetMetaStatus) {
        return `Archivage impossible: ce statut est le dernier actif du méta-statut ${targetMetaStatus}. Ajoutez ou conservez au moins un statut actif pour ce méta-statut.`;
      }
    }
    return message;
  }
}
